import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbItem } from '../../models/breadcrumb-item.model';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translate = inject(TranslateService);

  breadcrumbs$ = new BehaviorSubject<BreadcrumbItem[]>([]);

  constructor() {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.breadcrumbs$.next(this.buildBreadcrumbs()));
  }

  private buildBreadcrumbs(): BreadcrumbItem[] {
    const crumbs: BreadcrumbItem[] = [{ label: this.translate.instant('COMMON.HOME'), url: '/dashboard' }];

    const snapshots = [];
    let route = this.activatedRoute.root;
    while (route.firstChild) {
      route = route.firstChild;
      snapshots.push(route.snapshot);
    }

    let url = '';
    snapshots.forEach((snapshot) => {
      if (snapshot.url.length) {
        let segment = snapshot.url.map(s => s.path).join('/');
        const routeConfigPath = snapshot.routeConfig?.path;
        if (routeConfigPath && routeConfigPath.includes(':')) {
          const paramKeys = Object.keys(snapshot.params);
          paramKeys.forEach(key => {
            const value = snapshot.params[key];
            if (!segment.includes(value)) segment += '/' + value;
          });
        }
        url += '/' + segment;
      }

      const title = snapshot.title;
      if (!title) return;
      if (snapshot.data?.['breadcrumb'] === false) return;

      const isLeaf = !this.hasActiveUrlDescendant(snapshot);
      const noLink = snapshot.data?.['breadcrumbNoLink'];
      const disableParentLink = snapshot?.data?.['disableParentCrumbLink'];
      if (disableParentLink && crumbs.length > 0) {
        const lastCrumb = crumbs[crumbs.length - 1];
        if (lastCrumb.url)
          lastCrumb.url = null;
      }
      crumbs.push({
        label: snapshot.data?.['title'] ? this.translate.instant(snapshot.data['title']) : this.translate.instant(title),
        url: isLeaf || noLink ? null : url
      });
    });
    return crumbs;
  }

  private hasActiveUrlDescendant(snapshot: ActivatedRouteSnapshot): boolean {
    for (const child of snapshot.children) {
      if (child.url.length) return true;
      if (this.hasActiveUrlDescendant(child)) return true;
    }
    return false;
  }

  setLeafSuffix(suffix: string, copyValue?: string): void {
    const crumbs = this.breadcrumbs$.value;
    if (!crumbs.length) return;

    const updated = [...crumbs];
    const leaf = { ...updated[updated.length - 1] };
    leaf.suffix = suffix;
    leaf.copyValue = copyValue;
    updated[updated.length - 1] = leaf;
    this.breadcrumbs$.next(updated);
  }

  clearLeafSuffix(): void {
    const crumbs = this.breadcrumbs$.value;
    if (!crumbs.length) return;

    const updated = [...crumbs];
    const leaf = { ...updated[updated.length - 1] };
    delete leaf.suffix;
    delete leaf.copyValue;
    updated[updated.length - 1] = leaf;
    this.breadcrumbs$.next(updated);
  }
}
